import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { Draft } from '../../../lib/types';
import { reorderCategories, type DraftStore } from '../store/draftStore';
import { Hilfe } from '../../help/Help';
import { t } from '../../../lib/i18n';
import type { TopicSlug } from '../../help/topics';

export type Selection =
  | { kind: 'texts' }
  | { kind: 'links' }
  | { kind: 'logo' }
  | { kind: 'layout' }
  | { kind: 'spacing' }
  | { kind: 'theme' }
  | { kind: 'typo' }
  | { kind: 'buttons' }
  | { kind: 'behaviour' }
  | { kind: 'prefs' }
  | { kind: 'badge' }
  | { kind: 'category'; slug: string };

const sameSelection = (a: Selection | null, b: Selection): boolean =>
  a !== null && a.kind === b.kind && (a.kind !== 'category' || a.slug === (b as { slug: string }).slug);

/**
 * The structure panel (Doc 04 §5.1).
 *
 * Category order is drag-and-drop because it is the order visitors read them in, and
 * typing sort_order numbers into a form is a poor way to express "marketing goes last".
 */
export function TreePanel({
  store,
  draft,
  lang,
  selection,
  onSelect,
}: {
  store: DraftStore;
  draft: Draft;
  lang: string;
  /** null while the list is the whole panel — then nothing is the current section. */
  selection: Selection | null;
  onSelect: (s: Selection) => void;
}) {
  const apply = store((s) => s.apply);
  const sensors = useSensors(
    // A small distance threshold, so a click to select is not read as a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = draft.categories.findIndex((c) => c.slug === active.id);
    const to = draft.categories.findIndex((c) => c.slug === over.id);
    if (from < 0 || to < 0) return;
    apply(reorderCategories(from, to));
  }

  // The marker sits beside the button rather than inside it: nesting a button in a button
  // is invalid HTML, and the browser silently drops one of them.
  const Item = ({ sel, label, help }: { sel: Selection; label: string; help?: TopicSlug }) => (
    <div className={`flex w-full items-center border-l-2 pr-1 transition-colors ${
      sameSelection(selection, sel)
        ? 'border-[var(--color-brand)] bg-[var(--color-sunken)]'
        : 'border-transparent hover:border-[var(--color-line)]'
    }`}>
      <button
        onClick={() => onSelect(sel)}
        className={`flex-1 px-2 py-1.5 text-left text-[13px] transition-colors ${
          sameSelection(selection, sel)
            ? 'font-medium'
            : 'text-[var(--color-muted)] hover:text-[var(--color-ink)]'
        }`}
      >
        {label}
      </button>
      {help && <Hilfe to={help} />}
    </div>
  );

  return (
    <nav className="space-y-4 text-sm" aria-label={t('Struktur')}>
      <Section title={t('Inhalt')} help="banner-inhalt">
        <Item sel={{ kind: 'texts' }} label={t('Titel & Text')} />
        <Item sel={{ kind: 'links' }} label={t('Rechtliche Links')} />
        <Item sel={{ kind: 'logo' }} label={t('Logo')} />
      </Section>

      <Section title={t('Kategorien ({n})', { n: draft.categories.length })} help="kategorien">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={draft.categories.map((c) => c.slug)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-0.5">
              {draft.categories.map((c) => (
                <SortableCategory
                  key={c.slug}
                  slug={c.slug}
                  label={c.labels[lang]?.label || c.slug}
                  locked={c.required}
                  active={sameSelection(selection, { kind: 'category', slug: c.slug })}
                  onSelect={() => onSelect({ kind: 'category', slug: c.slug })}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      </Section>

      <Section title={t('Darstellung')} help="darstellung">
        <Item sel={{ kind: 'layout' }} label={t('Layout & Position')} />
        <Item sel={{ kind: 'spacing' }} label={t('Größe & Abstände')} />
        <Item sel={{ kind: 'theme' }} label={t('Farben & Form')} />
        <Item sel={{ kind: 'typo' }} label={t('Schrift')} />
        <Item sel={{ kind: 'buttons' }} label={t('Buttons')} help="buttons" />
        <Item sel={{ kind: 'badge' }} label={t('Widerruf-Badge')} help="widerruf" />
      </Section>

      <Section title={t('Verhalten')}>
        <Item sel={{ kind: 'behaviour' }} label={t('Overlay & Schließen')} />
        <Item sel={{ kind: 'prefs' }} label={t('Auswahl-Dialog')} help="auswahl-dialog" />
      </Section>
    </nav>
  );
}

function Section({ title, children, help }: {
  title: string; children: React.ReactNode; help?: TopicSlug;
}) {
  return (
    <div>
      <div className="eyebrow mb-1.5 flex items-center px-2">
        {title}
        {help && <Hilfe to={help} />}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function SortableCategory({
  slug, label, locked, active, onSelect,
}: {
  slug: string; label: string; locked: boolean; active: boolean; onSelect: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slug });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1 border-l-2 ${isDragging ? 'opacity-60' : ''} ${
        active
          ? 'border-[var(--color-brand)] bg-[var(--color-sunken)]'
          : 'border-transparent hover:border-[var(--color-line)]'
      }`}
    >
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab px-1 text-[var(--color-muted)] active:cursor-grabbing"
        aria-label={t('{label} verschieben', { label })}
        title={t('Ziehen zum Umsortieren')}
      >
        ⠿
      </span>
      <button onClick={onSelect} className={`flex-1 py-1.5 text-left text-[13px] ${active ? 'font-medium' : 'text-[var(--color-muted)]'}`}>
        {label}
      </button>
      {/* necessary cannot be reordered out of meaning or switched off, so it says so. */}
      {locked && (
        <span className="pr-2 text-[10px] tracking-wide text-[var(--color-muted)]"
          title={t('Diese Kategorie ist immer aktiv und lässt sich nicht abwählen.')}>
          {t('immer aktiv')}
        </span>
      )}
    </li>
  );
}
